import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuMinus, LuPlus, LuNavigation } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import MedioViaje, { useMedioViaje } from '../ui/MedioViaje';
import MapaLeaflet, { linkRuta } from '../mapa/MapaLeaflet';
import ParadasArmadas from './ParadasArmadas';
import OpcionesArmarDia from './OpcionesArmarDia';
import AvisosArmarDia from './AvisosArmarDia';
import { diaCorto, diaLargo } from '../empresa/diasRuta';
import { getTodayISO } from '../../utils/dateUtils';

// Armar el día solo (25-oct-2026). Elegís el día, el máximo de equipos, cómo viajás y el técnico; la app
// elige de todos los pedidos sin agendar los que van, primero los urgentes y después lo que más espera,
// respetando cuándo pueden recibir, la jornada y lo que el técnico ya tiene ese día. Lo revisás (podés
// sacar alguno y se vuelve a armar) y lo agendás: cada pedido en su franja, con el aviso de siempre.
const CLAVE_TOPE = 'armar-dia-tope';
const ROTULO = 'block text-label font-black uppercase tracking-widest text-muted mb-1.5';
const plural = (n, a, b) => `${n} ${n === 1 ? a : b}`;

function dias() {
    const hoy = getTodayISO();
    const [y, m, d] = hoy.split('-').map(Number);
    const out = [];
    const f = new Date(y, m - 1, d);
    while (out.length < 7) {
        if (f.getDay() !== 0) out.push(`${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`);
        f.setDate(f.getDate() + 1);
    }
    return out;
}

// soloPedidos (25-oct-2026): los pedidos elegidos en Trabajos → "Ver ruta": entran todos, solo esos, ordenados.
// fechaInicial (26-oct-2026): abierto desde el calendario de zonas, en ese día.
export default function ArmarDiaSheet({ tecnicos = [], soloPedidos = null, fechaInicial = null, onCerrar, onListo }) {
    const solo = soloPedidos?.length ? soloPedidos : null;
    const opciones = useMemo(dias, []);
    const [fecha, setFecha] = useState(() => (fechaInicial && opciones.includes(fechaInicial) ? fechaInicial : null) || opciones[getTodayISO() === opciones[0] && new Date().getHours() >= 12 ? 1 : 0]);
    const [tope, setTope] = useState(() => { try { return Number(localStorage.getItem(CLAVE_TOPE)) || 5; } catch { return 5; } });
    const [medio, setMedio] = useMedioViaje();
    const [tecnicoId, setTecnicoId] = useState('');
    // 27-oct-2026: "Su recorrido" = solo lo que ese técnico ya tiene asignado, en el mejor orden.
    // "Sumar pedidos" = además completa el día con pedidos sin agendar (lo de siempre, para Lucas).
    const [modo, setModo] = useState(null);   // null = según el técnico
    const [excluir, setExcluir] = useState([]);
    const [incluir, setIncluir] = useState([]);   // sumados a mano desde "Quedan para otro día"
    // Zona: la del día en el calendario, salvo que la cambies para ese día (26-oct-2026; antes quedaba la última usada)
    const [zonaManual, setZonaManual] = useState({});
    const [salidaId, setSalidaId] = useState('');  // '' = la app elige (camioneta → depósito)
    const [salidaDir, setSalidaDir] = useState(''); // otra dirección, solo para este día
    const [guardandoSalida, setGuardandoSalida] = useState(false);
    const [plan, setPlan] = useState(null);
    const manual = fecha in zonaManual;
    const zonaPedida = manual ? zonaManual[fecha] : undefined;
    // Si el día que abre ya está cerrado en el calendario (sus reservas ya se agendaron), pasa al siguiente (26-oct-2026)
    const [saltado, setSaltado] = useState(!!fechaInicial);
    useEffect(() => {
        if (saltado || !plan?.diaZona) return;
        setSaltado(true);
        const i = opciones.indexOf(fecha);
        if (plan.diaZona.cerrado && i >= 0 && i < opciones.length - 1) setFecha(opciones[i + 1]);
    }, [plan, saltado, fecha, opciones]);
    const zona = manual ? zonaPedida : (plan?.zona ?? null);
    const tecSel = tecnicos.find(t => String(t.id) === String(tecnicoId));
    const esLucas = /lucas\s+brandan/i.test(tecSel?.nombre || '');
    const suyo = !solo && (modo ? modo === 'suyo' : !esLucas);
    const nombreTec = (tecSel?.nombre || 'el técnico').split(' ')[0];
    const [armando, setArmando] = useState(false);
    const [agendando, setAgendando] = useState(false);

    // Técnico por defecto: Lucas (el que hace los trabajos de empresa)
    useEffect(() => {
        if (tecnicoId || !tecnicos.length) return;
        const lucas = tecnicos.find(t => /lucas\s+brandan/i.test(t.nombre || '')) || tecnicos[0];
        setTecnicoId(String(lucas.id));
    }, [tecnicos, tecnicoId]);

    const cambiarZona = z => setZonaManual(m => ({ ...m, [fecha]: z }));
    const sacar = id => { setExcluir(x => [...x, id]); setIncluir(x => x.filter(y => y !== id)); };
    const sumar = id => { setIncluir(x => [...x, id]); setExcluir(x => x.filter(y => y !== id)); };
    const cambiarTope = n => { const v = Math.max(1, Math.min(20, n)); setTope(v); try { localStorage.setItem(CLAVE_TOPE, String(v)); } catch { /* sin storage */ } };

    useEffect(() => {
        if (!fecha || (tecnicos.length && !tecnicoId)) return undefined;
        let vivo = true;
        setArmando(true);
        api.post('/mapa/armar-dia', { fecha, topeEquipos: solo || suyo ? undefined : tope, medio, tecnicoId: tecnicoId ? Number(tecnicoId) : undefined, excluir, incluir,
            soloAsignadas: suyo || undefined,
            ...(solo ? { zona: null } : manual ? { zona: zonaPedida } : {}), pedidoIds: solo || undefined, salida: salidaId || undefined, salidaDireccion: salidaId === 'DIR' ? salidaDir : undefined })
            .then(r => { if (vivo) setPlan(r.data); })
            // Si falla (ej. no encontró la otra dirección) queda lo anterior
            .catch(e => { if (vivo) { toast.error(e?.response?.data?.mensaje || 'No se pudo armar el día'); if (salidaId === 'DIR') setSalidaId(''); } })
            .finally(() => { if (vivo) setArmando(false); });
        return () => { vivo = false; };
    }, [fecha, tope, medio, tecnicoId, excluir, incluir, manual, zonaPedida, salidaId, salidaDir, tecnicos.length, solo, suyo]);

    const otraDireccion = d => { setSalidaDir(d); setSalidaId('DIR'); return true; };
    const guardarDeposito = async (clienteId, d) => {
        setGuardandoSalida(true);
        try {
            await api.put(`/pedidos-empresa/portal/${clienteId}`, { salidaRuta: d });
            toast.success('Depósito guardado');
            setSalidaId(`DEP-${clienteId}`);
            return true;
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); return false; }
        finally { setGuardandoSalida(false); }
    };

    // Salidas guardadas (26-oct-2026): ej. la sede de Nicaragua, para elegirla siempre
    const guardarSalida = async (d) => {
        setGuardandoSalida(true);
        try {
            const r = await api.post('/mapa/salidas', { direccion: d });
            toast.success('Salida guardada');
            setSalidaId(`GUA-${(r.data.salidas || []).length - 1}`);
            return true;
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); return false; }
        finally { setGuardandoSalida(false); }
    };
    const quitarSalida = async (d) => {
        setGuardandoSalida(true);
        try {
            await api.delete('/mapa/salidas', { data: { direccion: d } });
            toast.success('Salida sacada de las guardadas');
            setSalidaId('BASE');
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo sacar'); }
        finally { setGuardandoSalida(false); }
    };

    const nuevos = (plan?.paradas || []).filter(p => p.tipo === 'PEDIDO');
    const agendar = async () => {
        if (!tecnicoId) { toast.error('Elegí el técnico'); return; }
        setAgendando(true);
        try {
            const r = await api.post('/mapa/agendar', { fecha, tecnicoId: Number(tecnicoId), medio, paradas: nuevos });
            toast.success(`${plural(r.data.creadas.length, 'pedido agendado', 'pedidos agendados')} para el ${diaLargo(fecha)}`);
            onListo();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo agendar');
            setAgendando(false);
        }
    };

    // Su recorrido: guarda el orden armado (las mismas horas, repartidas en este orden)
    const visitas = (plan?.paradas || []).filter(p => p.tipo === 'VISITA' && p.ordenId);
    const guardarOrden = async () => {
        setAgendando(true);
        try {
            await api.post('/mapa/reordenar', { ordenIds: visitas.map(p => p.ordenId) });
            toast.success(`Recorrido de ${nombreTec} guardado en este orden`);
            onListo();
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || 'No se pudo guardar el orden');
            setAgendando(false);
        }
    };

    const salida = plan?.salida;
    const puntos = (plan?.paradas || []).map((p, i) => ({ id: p.pedidoId ? `p${p.pedidoId}` : `o${p.ordenId}`, lat: p.lat, lng: p.lng,
        etiqueta: String(i + 1), titulo: p.nombre, color: p.tipo === 'VISITA' ? '#64748B' : '#C9341F' }));
    if (salida?.lat != null && puntos.length) puntos.push({ id: 'salida', lat: salida.lat, lng: salida.lng, etiqueta: 'S', color: '#1C1917', titulo: salida.nombre });
    // Google Maps con las coordenadas (con el texto se iba a cualquier lado) y desde donde sale
    const google = plan?.paradas?.length ? linkRuta(plan.paradas, salida) : null;
    const linea = salida?.lat != null && puntos.length > 1 ? [[salida.lat, salida.lng], ...(plan.paradas || []).map(p => [p.lat, p.lng])] : null;

    const resumen = plan && plan.paradas.length ? [
        plural(plan.equipos, 'equipo', 'equipos') + (plan.equiposAgendados ? ` + ${plan.equiposAgendados} ya agendado${plan.equiposAgendados !== 1 ? 's' : ''}` : ''),
        `${String(plan.kmTotal).replace('.', ',')} km`, plan.terminaA ? `termina ~${plan.terminaA}${plan.finJornada ? ` (jornada hasta ${plan.finJornada})` : ''}` : null,
        salida?.nombre ? `sale de ${salida.nombre}` : null,
    ].filter(Boolean).join(' · ') : null;

    return (
        <ModalShell titulo={solo ? 'Ruta de los elegidos' : suyo ? `Recorrido de ${nombreTec}` : 'Armar día'}
            subtitulo={!plan ? 'Cargando…' : suyo ? `${plural(visitas.length, 'visita asignada', 'visitas asignadas')} en el mejor orden` : solo ? `${plural(solo.length, 'pedido', 'pedidos')} en el mejor orden` : `${plural(plan.pendientes, 'pedido sin agendar', 'pedidos sin agendar')} · primero lo urgente, después lo que más espera y queda cerca`}
            onCerrar={onCerrar} ancho="md:max-w-2xl"
            pie={
                <div className="flex gap-2">
                    <button type="button" onClick={onCerrar} className="flex-1 h-12 rounded-xl bg-chip text-secondary text-label font-black">Cancelar</button>
                    {suyo ? (
                        <button type="button" onClick={guardarOrden} disabled={agendando || armando || visitas.length < 2}
                            className="flex-[2] h-12 rounded-xl bg-brand-red text-white text-label font-black disabled:opacity-40 active:scale-95">
                            {agendando ? 'Guardando…' : visitas.length >= 2 ? 'Guardar este orden' : 'Nada para ordenar'}
                        </button>
                    ) : (
                        <button type="button" onClick={agendar} disabled={agendando || armando || !nuevos.length}
                            className="flex-[2] h-12 rounded-xl bg-brand-red text-white text-label font-black disabled:opacity-40 active:scale-95">
                            {agendando ? 'Agendando…' : nuevos.length ? `Agendar ${plural(nuevos.length, 'pedido', 'pedidos')}` : 'Nada para agendar'}
                        </button>
                    )}
                </div>
            }>
            <div className="space-y-4">
                <div>
                    <span className={ROTULO}>Día</span>
                    <div className="-mx-1 px-1 flex gap-1.5 overflow-x-auto pb-0.5">
                        {opciones.map(f => {
                            const c = diaCorto(f);
                            return (
                                <button key={f} type="button" onClick={() => { setSaltado(true); setFecha(f); setExcluir([]); setIncluir([]); }} aria-pressed={f === fecha}
                                    className={`shrink-0 flex-1 min-w-[3.25rem] h-14 rounded-xl inline-flex flex-col items-center justify-center ${f === fecha ? 'bg-ink text-page' : 'bg-panel text-secondary'}`}>
                                    <span className="text-caption font-bold uppercase">{f === getTodayISO() ? 'hoy' : c.dia}</span>
                                    <span className="text-body-lg font-black leading-none">{c.num}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className={`grid gap-3 ${solo || suyo ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {!solo && !suyo && <div>
                        <span className={ROTULO}>Máximo de equipos</span>
                        <div className="flex items-center gap-1.5">
                            <button type="button" onClick={() => cambiarTope(tope - 1)} aria-label="Uno menos" className="w-11 h-11 rounded-xl bg-chip text-ink inline-flex items-center justify-center active:scale-95"><LuMinus size={16} /></button>
                            <span className="flex-1 text-center text-body-lg font-black text-ink">{tope}</span>
                            <button type="button" onClick={() => cambiarTope(tope + 1)} aria-label="Uno más" className="w-11 h-11 rounded-xl bg-chip text-ink inline-flex items-center justify-center active:scale-95"><LuPlus size={16} /></button>
                        </div>
                    </div>}
                    <label className="block min-w-0">
                        <span className={ROTULO}>Técnico</span>
                        <select value={tecnicoId} onChange={e => { setTecnicoId(e.target.value); setModo(null); setExcluir([]); setIncluir([]); }} className="w-full h-11 px-3 rounded-xl bg-chip text-ink text-body font-bold outline-none">
                            {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                        </select>
                    </label>
                </div>
                {!solo && (
                    <div>
                        <span className={ROTULO}>Qué armar</span>
                        <div className="grid grid-cols-2 gap-1.5 p-1 rounded-xl bg-panel">
                            {[['suyo', `Solo lo de ${nombreTec}`], ['sumar', 'Sumar pedidos sin agendar']].map(([id, txt]) => (
                                <button key={id} type="button" aria-pressed={(id === 'suyo') === suyo}
                                    onClick={() => { setModo(id); setExcluir([]); setIncluir([]); }}
                                    className={`h-10 px-2 rounded-lg text-label font-black ${(id === 'suyo') === suyo ? 'bg-ink text-page' : 'text-secondary'}`}>{txt}</button>
                            ))}
                        </div>
                        <p className="mt-1.5 px-1 text-caption text-muted">
                            {suyo ? `Ordena las visitas que ${nombreTec} ya tiene asignadas ese día. No suma nada nuevo.`
                                : `Completa el día de ${nombreTec} con pedidos que todavía no tienen técnico.`}
                        </p>
                    </div>
                )}
                <MedioViaje valor={medio} onChange={m => { setMedio(m); setSalidaId(''); }} />
                <OpcionesArmarDia plan={plan} zona={zona} sinZona={!!solo} onZona={cambiarZona} onSalida={setSalidaId} onOtraDireccion={otraDireccion}
                    onGuardarDeposito={guardarDeposito} onGuardarSalida={guardarSalida} onQuitarSalida={quitarSalida} guardando={guardandoSalida || armando} />

                {plan && (
                    <>
                        {!solo && !suyo && plan.diaZona && (
                            <p className="px-3 py-2.5 rounded-xl bg-panel text-caption text-secondary">
                                <b className="text-ink">En el calendario, el {diaLargo(fecha)} es {plan.diaZona.zona === 'LIBRE' ? 'libre (cualquier zona)' : plan.diaZona.zona === 'NO' ? 'sin trabajo' : `de ${plan.diaZona.nombre || plan.diaZona.zona}`}.</b>
                                {plan.reservados > 0 ? ` La empresa reservó ${plural(plan.reservados, 'pedido', 'pedidos')} para este día: ya están adentro, marcados "Reservado".` : ' La empresa no reservó nada para este día.'}
                            </p>
                        )}
                        {!solo && !suyo && <AvisosArmarDia plan={plan} tope={tope} onTope={cambiarTope} onZona={cambiarZona} />}
                        {resumen && (
                            <div className="flex items-center gap-2 px-1">
                                <p className="flex-1 min-w-0 text-caption font-bold text-secondary">{resumen}</p>
                                {google && (
                                    <a href={google} target="_blank" rel="noreferrer"
                                        className="h-9 px-3 shrink-0 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center gap-1.5 active:scale-95">
                                        <LuNavigation size={14} /> Google Maps
                                    </a>
                                )}
                            </div>
                        )}
                        {puntos.length > 0 && (
                            <div className="relative isolate rounded-2xl overflow-hidden border border-black/[0.06] dark:border-white/[0.06]">
                                <MapaLeaflet puntos={puntos} lineas={linea} ajustarKey={`${fecha}-${puntos.map(p => p.id).join(',')}`} className="w-full h-[220px] md:h-[280px]" />
                            </div>
                        )}
                        {/* 26-oct-2026: Google nombra la salida por la calle de ese punto (ej. "Potigian, Lanús"); es el mismo lugar */}
                        {puntos.length > 0 && salida?.lat != null && (
                            <p className="-mt-2 px-1 text-caption text-muted">
                                <b className="text-secondary">S</b> = sale de {salida.nombre || 'la base'}. En Google Maps puede figurar con el nombre de la calle de ese punto: es el mismo lugar.
                            </p>
                        )}
                        <div className={armando ? 'opacity-50 pointer-events-none' : ''}>
                            <ParadasArmadas plan={plan} guardando={armando || agendando} onSacar={sacar} onSumar={sumar} sinSumados={!!solo}
                                vacio={suyo ? `${nombreTec} no tiene visitas asignadas ese día.` : null} />
                        </div>
                        {excluir.length > 0 && (
                            <button type="button" onClick={() => setExcluir([])} className="text-caption font-bold text-secondary underline px-1">
                                Volver a sumar {plural(excluir.length, 'el que saqué', 'los que saqué')}
                            </button>
                        )}
                    </>
                )}
                {!plan && armando && <p className="py-10 text-center text-body text-muted">Armando el día…</p>}
            </div>
        </ModalShell>
    );
}
